import { Box, Button, Chip, FormHelperText, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import FormikControl from "../../../components/common/FormikControl";
import { useDispatch, useSelector } from "react-redux";
import { addLabel, updateLabel } from "../../../slices/formbuildermaster";
import { limit, page } from "../../../../utils/utils";
import { useState } from "react";
import FormField from "../../../components/styled/FormField";

const validationSchema = Yup.object().shape({
    label: Yup.string()
        .required("Label is required"),
    api_url: Yup.string()
        .required("API URL is required")
        .matches(
            /^\/api\/.*$/,
            "API URL must start with /api/"
        ),
});
const AddLabelForm = ({ editData = null, onSuccess }) => {
    const dispatch = useDispatch();
    const [newParam, setNewParam] = useState("");
    const { isLoading } = useSelector((state) => state.bmrFormBuilderMaster);
    const initialValues = {
        label: editData?.label ?? "",
        api_url: editData?.api_url ?? "",
        depend_params: editData?.depend_params ?? [],
        default_query_params: editData?.default_query_params ?? {},
    };

    const parseApiUrl = (url) => {
        try {
            const [baseUrl, queryString] = url.split("?");

            const searchParams = new URLSearchParams(queryString);

            const depend_params = [];
            const default_query_params = {};

            searchParams.forEach((value, key) => {
                if (key === "page") {
                    default_query_params.page = page;
                } else if (key === "limit") {
                    default_query_params.limit = limit;
                } else if (key === "sort_by") {
                    if (value && value.trim() !== "") {
                        default_query_params.sort_by = value;
                    }
                } else if (key === "order_by") {
                    if (value && value.trim() !== "") {
                        default_query_params.order_by = value;
                    }
                } else {
                    depend_params.push(key);
                }
            });

            return {
                api_url: baseUrl,
                depend_params,
                default_query_params,
            };
        } catch (err) {
            return {
                api_url: url,
                depend_params: [],
                default_query_params: {},
            };
        }
    };

    const handleSubmit = async (values) => {
        const payload = {
            label: values.label,
            api_url: values.api_url,
            depend_params: values.depend_params || [],
            default_query_params: values.default_query_params || {},
        };

        if (editData) {
            await dispatch(updateLabel({ data: payload, editId: editData.id }));
        } else {
            await dispatch(addLabel(payload));
        }

        if (onSuccess) onSuccess();
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });

    const formOptions = [
        {
            label: "Label",
            type: "text",
            name: "label",
            required: true,
            placeholder: "Enter Label",
        },
        {
            label: "API URL",
            type: "text",
            name: "api_url",
            required: true,
            placeholder: "Enter API URL",
            helperText: "e.g., /api/v1/resource_name",
        },
    ];

const handleApiUrlChange = (e) => {
    const value = e.target.value;

    if (!value || value.trim() === "") {
        formik.setFieldValue("api_url", "");
        formik.setFieldValue("depend_params", []);
        formik.setFieldValue("default_query_params", {});
        return;
    }

    if (value.includes("?")) {
        const [baseUrl, queryString] = value.split("?");

        if (queryString && queryString.trim().length > 0) {
            const parsed = parseApiUrl(value);
            formik.setFieldValue("api_url", parsed.api_url);
            formik.setFieldValue("depend_params", parsed.depend_params);
            formik.setFieldValue("default_query_params", parsed.default_query_params);
        } else {
            formik.setFieldValue("api_url", baseUrl);
        }
    } else {
        formik.setFieldValue("api_url", value);
    }
};

const handleAddParam = () => {
    if (newParam && !formik.values.depend_params.includes(newParam)) {
        formik.setFieldValue("depend_params", [...formik.values.depend_params, newParam]);
        setNewParam("");
    }
};
    return (
      <Box>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} className="mb-3">
            {formOptions.map((props, key) => (
              <Grid key={key} item xs={12}>
                <FormikControl
                  formik={formik}
                  {...props}
                  {...(props.name === "api_url" && {
                    onChange: handleApiUrlChange,
                  })}
                />

                {props.name === "api_url" && (
                  <Box mt={1}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Dependency Params
                    </Typography>

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        mb={2}
                      >
                        <FormField
                          fullWidth
                          size="medium"
                          label="Add Dependency Parameter"
                          variant="outlined"
                          placeholder="e.g. searchData"
                          value={newParam}
                          onChange={(e) => setNewParam(e.target.value)}
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddParam}
                          sx={{ height: "40px", minWidth: "80px" }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {formik.values.depend_params.map((param) => (
                        <Chip
                          key={param}
                          label={param}
                          size="small"
                          onDelete={() => {
                            formik.setFieldValue(
                              "depend_params",
                              formik.values.depend_params.filter(
                                (item) => item !== param,
                              ),
                            );
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {props.name === "api_url" && (
                  <>
                    <FormHelperText>
                      Example:
                      /api/productList?page=1&limit=10&sort_by=&order_by=desc&searchData=
                    </FormHelperText>
                    <FormHelperText>
                      Note: page, limit, sort_by and order_by are default query
                      parameters and will not appear as chips.
                    </FormHelperText>
                  </>
                )}
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} className="mt-3">
            <Grid item xs={12}>
              <LoadingButton
                loading={isLoading}
                fullWidth
                type="submit"
                variant="contained"
              >
                {editData ? "Update" : "Submit"}
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
};

export default AddLabelForm;